import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Tag, AlertCircle, Loader2 } from 'lucide-react'; // Import icons
import apiService from '../../services/api';

const ProgramEncoreAI = ({ tmsId }) => {
  const [encoreData, setEncoreData] = useState(null);
  const [similarPrograms, setSimilarPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [error, setError] = useState(null);
  const [similarError, setSimilarError] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set()); // Track failed images
  const navigate = useNavigate();

  // Fetch Encore AI data when component mounts or tmsId changes
  useEffect(() => {
    const fetchEncoreData = async () => {
      if (!tmsId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getEncoreAI(tmsId);
        setEncoreData(data);
      } catch (err) {
        console.error('Error fetching Encore AI data:', err);
        if (err.response && err.response.status === 404) {
          setError('No Encore AI data found for this program');
        } else {
          setError('Failed to load Encore AI data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEncoreData();
  }, [tmsId]);

  // Fetch similar programs when component mounts or tmsId changes
  useEffect(() => {
    const fetchSimilarPrograms = async () => {
      if (!tmsId) {
        return;
      }

      try {
        setSimilarLoading(true);
        setSimilarError(null);
        const data = await apiService.getSimilarPrograms(tmsId, 20); // Get 10 similar programs
        setSimilarPrograms(data.data || []);
      } catch (err) {
        console.error('Error fetching similar programs:', err);
        if (err.response && err.response.status === 404) {
          setSimilarError('No similar programs found');
        } else {
          setSimilarError('Failed to load similar programs');
        }
      } finally {
        setSimilarLoading(false);
      }
    };

    fetchSimilarPrograms();
  }, [tmsId]);

  // Reset image errors when similar programs change
  useEffect(() => {
    setImageErrors(new Set());
  }, [similarPrograms]);

  // Helper function to parse JSON strings safely
  const parseJsonSafely = (jsonString) => {
    if (!jsonString) return [];
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('Failed to parse JSON:', jsonString);
      return [];
    }
  };

  // Helper function to format popularity score
  const formatPopularityScore = (score) => {
    if (score === null || score === undefined) return 'N/A';
    return `${Math.round(score * 100)}%`;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6 border border-gray-200">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-left">Encore AI</h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">Loading Encore AI data...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6 border border-gray-200">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-left">Encore AI</h3>
        <div className="flex items-center justify-center py-8">
          <AlertCircle className="h-8 w-8 text-red-500 mr-3" />
          <span className="text-red-600">{error}</span>
        </div>
      </div>
    );
  }

  // Show message if no data available
  if (!encoreData) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6 border border-gray-200">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-left">Encore AI</h3>
        <div className="flex items-center justify-center py-8">
          <AlertCircle className="h-8 w-8 text-gray-400 mr-3" />
          <span className="text-gray-600">No Encore AI data available for this program</span>
        </div>
      </div>
    );
  }

  // Parse genres from the updated data (pu_genres takes precedence over pc_genres)
  const genres = parseJsonSafely(encoreData.pu_genres || encoreData.pc_genres);
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6 border border-gray-200">
      <h3 className="text-2xl font-bold text-gray-800 mb-6 text-left">Encore AI</h3>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <p className="text-gray-700 mb-4 text-lg flex items-center">
            <Star className="h-5 w-5 text-yellow-500 mr-2" />
            <strong className="text-gray-900">Encore Computed Popularity Score:</strong> 
            <span className="text-green-600 font-semibold ml-2">
              {formatPopularityScore(encoreData.pu_rating || encoreData.pc_rating)}
            </span>
          </p>
          <div className="mb-4">
            <strong className="text-gray-900 text-lg block mb-3 flex items-center">
              <Tag className="h-5 w-5 text-purple-500 mr-2" />
              Tag Cloud Descriptors:
            </strong>
            <div className="flex flex-wrap gap-2">
              {genres.length > 0 ? (
                genres.map((genre, index) => (
                  <span key={index} className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full hover:bg-blue-200 transition-colors">
                    {genre}
                  </span>
                ))
              ) : (
                <span className="text-gray-500 italic">No genre tags available</span>
              )}
            </div>
          </div>
          
          {/* Display mood if available */}
          {(encoreData.pu_mood || encoreData.pc_mood) && (
            <div className="mb-4">
              <strong className="text-gray-900 text-lg block mb-2">Mood:</strong>
              <span className="bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full">
                {encoreData.pu_mood || encoreData.pc_mood}
              </span>
            </div>
          )}
        </div>
        <div className="lg:col-span-8">
          <h4 className="font-semibold text-lg text-gray-800 mb-3 text-left">Similar Programs:</h4>
          
          {/* Loading state for similar programs */}
          {similarLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
              <span className="text-gray-600">Loading similar programs...</span>
            </div>
          )}
          
          {/* Error state for similar programs */}
          {similarError && !similarLoading && (
            <div className="flex items-center py-4">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-600">{similarError}</span>
            </div>
          )}
          
          {/* Similar programs display */}
          {!similarLoading && !similarError && similarPrograms.length > 0 && (
            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              {similarPrograms.map((item, index) => {
                const imageKey = `${item.tmsid}-${index}`;
                const hasImageError = imageErrors.has(imageKey);

                const handleProgramClick = () => {
                  if (item.tmsid) {
                    navigate(`/program/${item.tmsid}`);
                  }
                };
                
                return (
                  <div 
                    key={item.tmsid || index} 
                    className="flex flex-col items-center text-center w-24 cursor-pointer"
                    onClick={handleProgramClick}
                  >
                    {hasImageError ? (
                      <div className="w-24 h-36 bg-gray-200 border border-gray-200 rounded-md shadow-sm flex items-center justify-center text-gray-500 text-xs">
                        No Image
                      </div>
                    ) : (
                      <img
                        src={item.image_url}
                        alt={item.title || `Similar Program ${index + 1}`}
                        className="w-24 h-36 object-cover border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow"
                        onError={() => {
                          setImageErrors(prev => new Set([...prev, imageKey]));
                        }}
                      />
                    )}
                    <span className="text-xs text-gray-800 mt-1 font-semibold leading-tight line-clamp-2">
                      {item.title || 'Unknown Title'}
                    </span>
                    <span className="text-xs text-gray-500">{item.tmsid}</span>
                    {item.similarity_score && (
                      <span className="text-xs text-green-600 font-medium">
                        {Math.round(item.similarity_score * 100)}% match
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* No similar programs found */}
          {!similarLoading && !similarError && similarPrograms.length === 0 && (
            <div className="flex items-center py-4">
              <AlertCircle className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-gray-600">No similar programs found</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramEncoreAI;
